import type ItemReadingPaneViewState from '../store/schema/ItemReadingPaneViewState';
import { getStore } from '../store/Store';
import { mutatorAction } from 'satcheljs';
import { secondaryTabsHaveId } from 'owa-tab-store';
import getPrimaryTabId from '../utils/getPrimaryTabId';
import { isItemInSxS } from 'owa-sxs-store/lib/utils/isItemInSxS';
import isItemInPreviewPaneViewState from '../utils/isItemInPreviewPaneViewState';

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const addLoadedItemReadingPaneViewState: (
    itemReadingPaneViewState: ItemReadingPaneViewState
) => void = mutatorAction(
    'addLoadedItemReadingPaneViewState',
    function (itemReadingPaneViewState: ItemReadingPaneViewState): void {
        const itemId = itemReadingPaneViewState.itemId;
        const { loadedItemReadingPaneViewStates } = getStore();
        if (!loadedItemReadingPaneViewStates.has(itemId)) {
            loadedItemReadingPaneViewStates.set(itemId, itemReadingPaneViewState);
        }
    }
);

export const setItemPreviewPaneViewStateMutator: (
    itemReadingPaneViewState: ItemReadingPaneViewState
) => void = mutatorAction(
    'setItemPreviewPaneViewStateMutator',
    function (itemReadingPaneViewState: ItemReadingPaneViewState): void {
        getStore().itemPreviewPaneViewState = itemReadingPaneViewState;
    }
);

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const releaseOrphanedLoadedItemViewStates: () => void = mutatorAction(
    'releaseOrphanedLoadedItemViewStates',
    function (): void {
        const {
            loadedItemReadingPaneViewStates,
            loadingProjectionIds,
            itemPreviewPaneViewState,
            itemPrintPaneViewStates,
        } = getStore();
        // Release any view states that is none of the following:
        // - is held by any tabs (primary or secondary)
        // - is currently being loaded for projection popout
        // - is the item preview pane
        // - is the item print pane
        // - is used in SxS view (item attachment or item reading pane)
        /* eslint-disable-next-line owa-custom-rules/forbid-foreach-with-variables-outside-of-function-scope -- (https://aka.ms/OWALintWiki)
         * https://dev.azure.com/outlookweb/Outlook%20Web/_wiki/wikis/Outlook%20Web.wiki/9650/Use-for-const-loop-of-instead-of-forEach
         *	> When using a forEach function call, avoid using variables outside of the scope of the function, use for (const item of array) instead
         *	> When using a forEach function call, avoid using variables outside of the scope of the function, use for (const item of array) instead */
        loadedItemReadingPaneViewStates.forEach((_viewState, loadedItemId) => {
            if (
                !(
                    loadedItemId == getPrimaryTabId() ||
                    secondaryTabsHaveId(loadedItemId) ||
                    loadingProjectionIds?.indexOf(loadedItemId) != -1 ||
                    loadedItemId == itemPreviewPaneViewState?.itemId ||
                    isItemInPreviewPaneViewState(loadedItemId) ||
                    isItemInSxS(loadedItemId)
                )
            ) {
                loadedItemReadingPaneViewStates.delete(loadedItemId);
            }
        });

        // Also release any unused view states from item print pane
        itemPrintPaneViewStates &&
            itemPrintPaneViewStates.forEach((viewState, tabId) => {
                const itemId = viewState.itemId;
                if (itemId && !(getPrimaryTabId() == itemId || secondaryTabsHaveId(itemId))) {
                    itemPrintPaneViewStates.delete(tabId);
                }
            });
    }
);

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const releaseLoadedItemViewState: (itemId: string) => void = mutatorAction(
    'releaseLoadedItemViewState',
    function (itemId: string): void {
        getStore().loadedItemReadingPaneViewStates.delete(itemId);
    }
);
